// src/components/RiskCodingAnalysis.jsx
import React, { useMemo } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Divider,
  Grid,
  Box,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ReportIcon from '@mui/icons-material/Report';

// Helpers para leer código/descripcion de estructuras distintas
const getCode = (item) => item?.code || item?.hcc || item?.icd10 || '';
const getDescription = (item) => item?.description || item?.desc || '—';

const getWeight = (item) => {
  const w = Number(item?.rafWeight);
  return Number.isFinite(w) ? w : 0;
};

const sumWeights = (arr) => (arr || []).reduce((s, x) => s + getWeight(x), 0);

// Calcula HCC “perdidos” (estaban en retro, no están en current)
const computeMissing = (retrospective, current) => {
  const currentCodes = new Set((current || []).map(getCode).filter(Boolean));

  return (retrospective || []).filter((item) => {
    const code = getCode(item);
    return code && !currentCodes.has(code);
  });
};

// Detecta condiciones con MEAT incompleto
const computeIncompleteMeat = (current) =>
  (current || []).filter((c) => {
    const monitoringDate = c?.meat?.monitoring?.date;
    const monitoringSource = c?.meat?.monitoring?.source;
    const treatmentMedication = c?.meat?.treatment?.medication;
    const treatmentDate = c?.meat?.treatment?.date;

    const hasMonitoring =
      monitoringDate &&
      monitoringDate !== 'N/A' &&
      monitoringSource &&
      monitoringSource.trim() !== '';

    const hasTreatment =
      treatmentMedication &&
      treatmentMedication !== 'N/A' &&
      treatmentMedication.trim() !== '' &&
      treatmentDate &&
      treatmentDate !== 'N/A';

    return !hasMonitoring || !hasTreatment;
  });

// extras = future - current (por code)
const computeFutureAdds = (future, current) => {
  const currentCodes = new Set((current || []).map(getCode).filter(Boolean));
  return (future || []).filter((item) => {
    const code = getCode(item);
    return code && !currentCodes.has(code);
  });
};

// qué falta documentar para capturar un HCC sugerido
const getMeatGaps = (c) => {
  const monitoringOk =
    c?.meat?.monitoring?.date &&
    c.meat.monitoring.date !== 'N/A' &&
    (c?.meat?.monitoring?.source || '').trim() !== '';

  const treatmentOk =
    (c?.meat?.treatment?.medication || '').trim() !== '' &&
    c.meat.treatment.medication !== 'N/A' &&
    c?.meat?.treatment?.date &&
    c.meat.treatment.date !== 'N/A';

  const gaps = [];
  if (!monitoringOk) gaps.push('monitoring');
  if (!treatmentOk) gaps.push('treatment');
  return gaps;
};

/**
 * hccCurrent: condiciones HCC actuales (salida IA para RAF actual)
 * hccFuture:  condiciones HCC futuras (proyección IA: current + sugeridas)
 * hccRetro:   condiciones históricas (claims/years previos) – opcional
 */
const RiskCodingAnalysis = ({
  hccCurrent = [],
  hccFuture = [],
  hccRetro = [],
}) => {
  const {
    retrospective,
    current,
    future,
    missing,
    incompleteMeat,
    futureAdds,
    totalCurrent,
    totalFuture,
    delta,
  } = useMemo(() => {
    const retro = Array.isArray(hccRetro) ? hccRetro : [];
    const curr = Array.isArray(hccCurrent) ? hccCurrent : [];

    // si no viene future, caemos a current (evita romper UI)
    const fut =
      Array.isArray(hccFuture) && hccFuture.length > 0 ? hccFuture : curr;

    const miss = computeMissing(retro, curr);
    const incomplete = computeIncompleteMeat(curr);

    // HCC adicionales que propone la IA para capturar el Future RAF
    const adds = computeFutureAdds(fut, curr).sort(
      (a, b) => getWeight(b) - getWeight(a)
    );

    const tCurr = sumWeights(curr);
    const tFut = sumWeights(fut);
    const d = tFut - tCurr;

    return {
      retrospective: retro,
      current: curr,
      future: fut,
      missing: miss,
      incompleteMeat: incomplete,
      futureAdds: adds,
      totalCurrent: tCurr,
      totalFuture: tFut,
      delta: d,
    };
  }, [hccCurrent, hccFuture, hccRetro]);

  const hasAnyData =
    retrospective.length > 0 || current.length > 0 || future.length > 0;

  return (
    <Card elevation={3} sx={{ borderRadius: 2, mb: 4 }}>
      <CardHeader
        title="Risk Coding Analysis"
        titleTypographyProps={{ variant: 'h5', align: 'center' }}
        action={<ReportIcon fontSize="large" color="primary" />}
      />
      <Divider />
      <CardContent>
        {!hasAnyData ? (
          <Typography variant="body2">
            Run &quot;Generate HCC with AI&quot; to see the risk coding analysis.
          </Typography>
        ) : (
          <>
            <Grid container spacing={2}>
              {/* Retrospective Horizon */}
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <HistoryIcon sx={{ mr: 1, color: 'secondary.main' }} />
                  <Typography variant="h6">
                    Retrospective Horizon (2023-2024)
                  </Typography>
                </Box>

                {retrospective.length === 0 ? (
                  <Typography variant="body2">
                    No historical HCC coding data available yet for this member.
                  </Typography>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>HCC Code</TableCell>
                        <TableCell>Description</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {retrospective.map((item, idx) => (
                        <TableRow key={`retro-${idx}`} hover>
                          <TableCell>{getCode(item)}</TableCell>
                          <TableCell>{getDescription(item)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Grid>

              {/* Current Horizon */}
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AccessTimeIcon sx={{ mr: 1, color: 'success.main' }} />
                  <Typography variant="h6">Current Horizon (2025)</Typography>
                </Box>

                {current.length === 0 ? (
                  <Typography variant="body2">
                    No active HCC conditions have been identified for the current year.
                  </Typography>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>HCC Code</TableCell>
                        <TableCell>Description</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {current.map((item, idx) => (
                        <TableRow key={`current-${idx}`} hover>
                          <TableCell>{getCode(item)}</TableCell>
                          <TableCell>{getDescription(item)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Grid>

              {/* Future Horizon */}
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AccessTimeIcon sx={{ mr: 1, color: 'info.main' }} />
                  <Typography variant="h6">Future Horizon (Projection)</Typography>
                </Box>

                {futureAdds.length === 0 ? (
                  <Typography variant="body2">
                    No additional AI-suggested HCC codes beyond current conditions.
                  </Typography>
                ) : (
                  <>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Additional RAF opportunity:&nbsp;
                      <strong>
                        {Number.isFinite(delta)
                          ? delta >= 0
                            ? `+${delta.toFixed(2)}`
                            : delta.toFixed(2)
                          : '0.00'}
                      </strong>
                    </Typography>

                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>HCC Code</TableCell>
                          <TableCell>Description</TableCell>
                          <TableCell align="right">RAF</TableCell>
                          <TableCell>Gaps</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {futureAdds.map((item, idx) => {
                          const gaps = getMeatGaps(item);
                          return (
                            <TableRow key={`future-add-${idx}`} hover>
                              <TableCell>{getCode(item)}</TableCell>
                              <TableCell>{getDescription(item)}</TableCell>
                              <TableCell align="right">
                                {getWeight(item).toFixed(2)}
                              </TableCell>
                              <TableCell>
                                {gaps.length ? gaps.join(' + ') : 'MEAT looks sufficient'}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </>
                )}
              </Grid>

              {/* Missing Codes (solo si hay retro) */}
              {retrospective.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mt: 2 }}>
                    HCC Codes Not Documented in 2025
                  </Typography>

                  {missing.length === 0 ? (
                    <Typography variant="body2">
                      All historical HCC codes have been documented again in the current year.
                    </Typography>
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>HCC Code</TableCell>
                          <TableCell>Description</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {missing.map((item, idx) => (
                          <TableRow key={`missing-${idx}`} hover>
                            <TableCell>{getCode(item)}</TableCell>
                            <TableCell>{getDescription(item)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </Grid>
              )}
            </Grid>

            {/* Suggestions */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Suggestions and Rationale:
              </Typography>

              {!retrospective.length &&
                !incompleteMeat.length &&
                !futureAdds.length && (
                  <Typography variant="body2">
                    With the current information, no specific gaps were detected.
                    Ensure that each active HCC condition has clear MEAT documentation
                    in the clinical notes before year-end.
                  </Typography>
                )}

              {/* Sugerencias por HCC históricos no recapturados */}
              {retrospective.length > 0 && missing.length > 0 && (
                <>
                  <Typography variant="body2" paragraph>
                    It is recommended to document the missing HCC codes in the
                    current horizon before year-end to ensure full RAF capture
                    and avoid risk score drop. In particular:
                  </Typography>
                  <ul>
                    {missing.map((item, idx) => (
                      <li key={`missing-sugg-${idx}`}>
                        <Typography variant="body2">
                          <strong>
                            {getCode(item)} – {getDescription(item)}
                          </strong>
                          : consider reviewing the problem list and clinical
                          notes to confirm whether the condition is still active
                          and requires documentation.
                        </Typography>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {/* Sugerencias por MEAT incompleto en el año actual */}
              {incompleteMeat.length > 0 && (
                <>
                  <Typography variant="body2" paragraph sx={{ mt: 2 }}>
                    The following conditions show incomplete MEAT documentation
                    (missing monitoring, treatment, or both). Strengthening documentation
                    for these codes can improve RAF validation:
                  </Typography>
                  <ul>
                    {incompleteMeat.map((c, idx) => {
                      const code = getCode(c);
                      const desc = getDescription(c);

                      const gaps = getMeatGaps(c);

                      return (
                        <li key={`meat-${idx}`}>
                          <Typography variant="body2">
                            <strong>
                              {code} – {desc}
                            </strong>
                            {': '}missing {gaps.join(' and ')} documentation in the current
                            notes. Consider updating Assessment &amp; Plan, labs, or medication
                            list accordingly.
                          </Typography>
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}

              {/* Sugerencias para capturar el Future RAF */}
              {futureAdds.length > 0 && (
                <>
                  <Typography variant="body2" paragraph sx={{ mt: 2 }}>
                    <strong>To reach the projected (Future) RAF</strong>, focus on documenting and validating
                    the following AI-suggested HCCs. Prioritize higher RAF weights first, and ensure MEAT is explicit
                    in Assessment &amp; Plan:
                  </Typography>
                  <ul>
                    {futureAdds.slice(0, 8).map((c, idx) => {
                      const gaps = getMeatGaps(c);
                      return (
                        <li key={`future-sugg-${idx}`}>
                          <Typography variant="body2">
                            <strong>
                              {getCode(c)} – {getDescription(c)}
                            </strong>
                            {getWeight(c) ? ` (RAF ${getWeight(c).toFixed(2)})` : ''}
                            {gaps.length
                              ? `: needs stronger ${gaps.join(' + ')} evidence. Add current status, supporting labs/vitals, and an active treatment/plan.`
                              : `: appears ready—confirm active status and ensure it is explicitly assessed and treated in current-year documentation.`}
                          </Typography>
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}

              <Typography variant="body2" sx={{ mt: 2 }}>
                Fully documenting MEAT (Monitoring, Evaluation/Assessment, Treatment) for each active condition
                will help meet validation criteria and improve overall RAF accuracy.
              </Typography>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default RiskCodingAnalysis;
